// PowerSync — Study Plan + sections/subsections + auto-linked materials.
import { usePowerSyncQuery } from "@/lib/powersync/use-query";

export interface LocalStudyPlan {
	id: string;
	user_id: string;
	name: string;
	title: string | null;
	description: string | null;
	exam_date: string | null;
	status: string;
	country: string | null;
	education_subsystem: string | null;
	source_type: string | null;
	progress_pct: number | null;
	total_sections: number | null;
	total_subsections: number | null;
	completed_sections: number | null;
	completed_subsections: number | null;
	generated_from_set_id: string | null;
	generated_from_template_id: string | null;
	/**
	 * Происхождение курса: `template` — запись из каталога / импорт программы
	 * платформы, `from-topic` / `from-materials` — генерация, `manual` — бриф и
	 * пустой курс. Колонки в синкаемой схеме есть давно; в типе не объявлялись,
	 * поэтому читатели их не видели (`placement-banner.tsx` — первый).
	 */
	template_id: string | null;
	template_slug: string | null;
	ai_model: string | null;
	prompt_version: string | null;
	generation_trace_id: string | null;
	created_at: string;
	updated_at: string;
	deleted_at: string | null;
}

export interface LocalStudySection {
	id: string;
	plan_id: string;
	title: string;
	description: string | null;
	order: number | null;
	sort_order: number | null;
	status: string;
	estimated_minutes: number | null;
	progress_percent: number | null;
	progress_pct: number | null;
}

export interface LocalStudySubsection {
	id: string;
	section_id: string;
	plan_id: string | null;
	title: string;
	description: string | null;
	order: number | null;
	sort_order: number | null;
	status: string;
	progress_percent: number | null;
	progress_pct: number | null;
	recommended_activities: string | null;
	total_activities: number | null;
	completed_activities: number | null;
}

export interface LocalStudySubsectionMaterial {
	id: string;
	subsection_id: string;
	material_id: string;
	chunk_id: string | null;
	similarity: number | null;
	auto_linked: number;
	created_at: string;
}

export function useStudyPlansQuery(userId: string | undefined) {
	return usePowerSyncQuery<LocalStudyPlan>(
		`SELECT * FROM study_plans
		   WHERE user_id = ? AND deleted_at IS NULL
		ORDER BY updated_at DESC`,
		[userId ?? ""],
	);
}

/** Все секции плана (для offline detail-shell без activities). */
export function useStudySectionsForPlanQuery(planId: string | undefined) {
	return usePowerSyncQuery<LocalStudySection>(
		`SELECT * FROM study_sections
		   WHERE plan_id = ?
		ORDER BY COALESCE(sort_order, "order") ASC`,
		[planId ?? ""],
	);
}

/** Все подсекции плана (через plan_id denorm или JOIN по секциям). */
export function useStudySubsectionsForPlanQuery(planId: string | undefined) {
	return usePowerSyncQuery<LocalStudySubsection>(
		`SELECT ss.* FROM study_subsections ss
		   JOIN study_sections s ON s.id = ss.section_id
		  WHERE s.plan_id = ?
		ORDER BY COALESCE(ss.sort_order, ss."order") ASC`,
		[planId ?? ""],
	);
}

export function useStudyPlanByIdQuery(planId: string | undefined) {
	return usePowerSyncQuery<LocalStudyPlan>(`SELECT * FROM study_plans WHERE id = ?`, [
		planId ?? "",
	]);
}

export function useStudySectionsByPlanQuery(planId: string | undefined) {
	return usePowerSyncQuery<LocalStudySection>(
		`SELECT * FROM study_sections
		   WHERE plan_id = ?
		ORDER BY "order" ASC`,
		[planId ?? ""],
	);
}

export function useStudySubsectionsBySectionQuery(sectionId: string | undefined) {
	return usePowerSyncQuery<LocalStudySubsection>(
		`SELECT * FROM study_subsections
		   WHERE section_id = ?
		ORDER BY "order" ASC`,
		[sectionId ?? ""],
	);
}

export function useStudySubsectionMaterialsQuery(subsectionId: string | undefined) {
	return usePowerSyncQuery<LocalStudySubsectionMaterial>(
		`SELECT * FROM study_subsection_materials
		   WHERE subsection_id = ?
		ORDER BY similarity DESC`,
		[subsectionId ?? ""],
	);
}
